import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Box, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField, useTheme } from "@mui/material";
import { CountrySelect } from "@/components/CountrySelect";
import { profileStore } from "@/stores/Stores";
import { roles } from "@/util/roles";
import { interests } from "@/util/interests";
import { industries } from "@/util/industries";
import getStyles from "./styles";

export const AdditionalInfo = observer(({ onChangeCountry = () => { } }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getStyles(theme);

  const userProfile = profileStore.profile;

  return (
    <Stack direction="column" sx={styles.section}>
      <Box sx={styles.item}>
        <TextField disabled fullWidth label={t("E-Mail")} value={userProfile.email} sx={styles.email} />
      </Box>
      <Box sx={styles.item}>
        <TextField
          fullWidth
          label={t("Job Title")}
          value={userProfile.jobTitle}
          onChange={e => {
            userProfile.jobTitle = e.target.value;
          }}
          sx={styles.jobTitle}
        />
      </Box>
      <Box sx={styles.item}>
        <FormControl fullWidth sx={styles.industry}>
          <InputLabel>{t("Industry")}</InputLabel>
          <Select
            labelId="industry-label"
            multiple={false}
            value={userProfile.industry}
            onChange={e => {
              userProfile.industry = e.target.value;
            }}
            error={profileStore.showErrors && profileStore.profile.industry === ""}
            label={t("Industry")}
          >
            {industries.map(industry => (
              <MenuItem key={`industry-${industry.value}`} value={industry.value}>
                {t(industry.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={styles.item}>
        <FormControl fullWidth sx={styles.role}>
          <InputLabel>{t("Role")}</InputLabel>
          <Select
            labelId="role-label"
            value={userProfile.role}
            multiple={false}
            onChange={e => {
              userProfile.role = e.target.value;
            }}
            error={profileStore.showErrors && profileStore.profile.role === ""}
            label={t("Role")}
          >
            {roles.map(role => (
              <MenuItem key={`role-${role.value}`} value={role.value}>
                {t(role.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={styles.item}>
        <FormControl fullWidth sx={styles.interests}>
          <InputLabel>{t("Interests")}</InputLabel>
          <Select
            labelId="interests-label"
            multiple
            value={userProfile?.interests || []}
            onChange={({ target: { value } }) => {
              userProfile.interests = typeof value === "string" ? value.split(",") : value;
            }}
            input={<OutlinedInput label={t("Interests")} />}
            renderValue={selected => {
              return selected.map(interest => {
                const interestObj = interests.find(i => i.value === interest);
                return interestObj ? t(interestObj.label) : interest;
              }).join(", ");
            }}
          >
            {interests.map(interest => (
              <MenuItem key={`interest-${interest.value}`} value={interest.value}>
                {t(interest.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={styles.item}>
        <TextField
          fullWidth
          label={t("Company name")}
          value={userProfile.companyName}
          onChange={e => {
            userProfile.companyName = e.target.value;
          }}
          sx={styles.companyName}
        />
      </Box>
      <Stack sx={styles.countryAndLanguage}>
        <CountrySelect
          onChange={onChangeCountry}
          fullWidth
          size="medium"
          selectedCountry={userProfile.country}
          error={profileStore.showErrors && profileStore.profile.country === ""}
          sx={styles.country}
        />
        <FormControl sx={styles.language}>
          <InputLabel id="language-label">{t("Language")}</InputLabel>
          <Select
            labelId="language-label"
            value={userProfile.locale.toUpperCase()}
            onChange={e => {
              userProfile.locale = e.target.value.toUpperCase();
            }}
            label="Language"
            error={profileStore.showErrors && profileStore.profile.locale === ""}
          >
            <MenuItem value="EN">English</MenuItem>
            <MenuItem value="DE">Deutsch</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
});
